import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import type {DragEndEvent, DragStartEvent} from "@dnd-kit/core";
import {restrictToHorizontalAxis} from "@dnd-kit/modifiers";
import {
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {PointerEvent as ReactPointerEvent, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {ServerTablePrintColumnLayout} from "./types";
import {movePrintColumn, resizePrintColumnsAcrossBoundary} from "./utils";

interface ResizeDrag {
    pointerId: number
    startX: number
    leftId: string
    rightId: string
    columnIds: string[]
    startWidths: Record<string, number>
    totalWidth: number
    ribbonWidth: number
    lastCommitAt: number
    pending: Record<string, number> | null
}

const HeaderCellContent = ({label}: {label: string}) => (
    <>
        <DragIndicatorIcon sx={{fontSize: 12, color: 'text.disabled', flexShrink: 0}}/>
        <Typography
            variant="caption"
            noWrap
            sx={{fontWeight: 700, lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}
        >
            {label}
        </Typography>
    </>
);

interface RibbonHeaderCellProps {
    id: string
    label: string
    widthPct: number
    showHandle: boolean
    onResizeStart: (event: ReactPointerEvent<HTMLDivElement>, leftId: string) => void
    onResizeMove: (event: ReactPointerEvent<HTMLDivElement>) => void
    onResizeEnd: () => void
}

function RibbonHeaderCell({id, label, widthPct, showHandle, onResizeStart, onResizeMove, onResizeEnd}: RibbonHeaderCellProps) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});

    return (
        <Box
            ref={setNodeRef}
            style={{transform: CSS.Transform.toString(transform), transition}}
            sx={{
                width: `${widthPct}%`,
                flexShrink: 0,
                position: 'relative',
                // The overlay is the moving copy, so the original remains as a layout placeholder.
                opacity: isDragging ? 0.35 : 1,
                bgcolor: 'grey.50',
                borderRight: showHandle ? '1px solid' : 0,
                borderColor: 'divider'
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                title={label}
                sx={{
                    px: 0.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    cursor: 'grab',
                    '&:active': {cursor: 'grabbing'},
                    userSelect: 'none',
                    // Prevent touch scrolling from stealing the drag gesture.
                    touchAction: 'none',
                    minWidth: 0
                }}
            >
                <HeaderCellContent label={label}/>
            </Box>

            {showHandle && (
                <Box
                    onPointerDown={event => onResizeStart(event, id)}
                    onPointerMove={onResizeMove}
                    onPointerUp={onResizeEnd}
                    onPointerCancel={onResizeEnd}
                    onLostPointerCapture={onResizeEnd}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: -6,
                        width: 12,
                        zIndex: 2,
                        cursor: 'col-resize',
                        touchAction: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&::after': {content: '""', width: '2px', height: '60%', borderRadius: 1},
                        '&:hover::after, &:active::after': {bgcolor: 'primary.main'}
                    }}
                />
            )}
        </Box>
    );
}

interface ServerTablePrintColumnRibbonProps {
    columns: ServerTablePrintColumnLayout[]
    minWidthFraction: number
    onOrderChange: (columnIds: string[]) => void
    onWidthsChange: (widths: Record<string, number>) => void
}

export function ServerTablePrintColumnRibbon({
    columns,
    minWidthFraction,
    onOrderChange,
    onWidthsChange
}: ServerTablePrintColumnRibbonProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const resizeRef = useRef<ResizeDrag | null>(null);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const totalWidth = columns.reduce((sum, column) => sum + column.width, 0) || 1;
    const activeColumn = activeDragId ? columns.find(column => column.id === activeDragId) : undefined;

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const handleDragStart = ({active}: DragStartEvent) => {
        setActiveDragId(String(active.id));
    };

    const handleDragEnd = ({active, over}: DragEndEvent) => {
        setActiveDragId(null);
        if (!over || active.id === over.id) return;
        onOrderChange(movePrintColumn(columns.map(column => column.id), String(active.id), String(over.id)));
    };

    const beginResize = (event: ReactPointerEvent<HTMLDivElement>, leftId: string) => {
        const ribbonWidth = containerRef.current?.clientWidth;
        const leftIndex = columns.findIndex(column => column.id === leftId);
        const rightId = columns[leftIndex + 1]?.id;
        if (!ribbonWidth || !rightId) return;

        event.preventDefault();
        event.stopPropagation();
        resizeRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            leftId,
            rightId,
            columnIds: columns.map(column => column.id),
            startWidths: Object.fromEntries(columns.map(column => [column.id, column.width])),
            totalWidth,
            ribbonWidth,
            lastCommitAt: 0,
            pending: null
        };
        // Pointer capture keeps resizing active outside the handle when supported.
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
            // Resizing still works while the pointer remains over the handle.
        }
    };

    const moveResize = (event: ReactPointerEvent<HTMLDivElement>) => {
        const drag = resizeRef.current;
        if (!drag || event.pointerId !== drag.pointerId) return;

        const widthDelta = ((event.clientX - drag.startX) / drag.ribbonWidth) * drag.totalWidth;
        const next = resizePrintColumnsAcrossBoundary(
            drag.startWidths,
            drag.columnIds,
            drag.leftId,
            drag.rightId,
            widthDelta,
            drag.totalWidth * minWidthFraction
        );

        // Throttling live commits keeps re-pagination smooth; endResize flushes the remainder.
        const now = Date.now();
        if (now - drag.lastCommitAt >= 80) {
            drag.lastCommitAt = now;
            drag.pending = null;
            onWidthsChange(next);
        } else {
            drag.pending = next;
        }
    };

    const endResize = () => {
        const drag = resizeRef.current;
        if (!drag) return;
        resizeRef.current = null;
        if (drag.pending) onWidthsChange(drag.pending);
    };

    return (
        <Box ref={containerRef}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragStart={handleDragStart}
                onDragCancel={() => setActiveDragId(null)}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={columns.map(column => column.id)} strategy={horizontalListSortingStrategy}>
                    <Box sx={{display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden'}}>
                        {columns.map((column, index) => (
                            <RibbonHeaderCell
                                key={column.id}
                                id={column.id}
                                label={column.label}
                                widthPct={(column.width / totalWidth) * 100}
                                showHandle={index < columns.length - 1}
                                onResizeStart={beginResize}
                                onResizeMove={moveResize}
                                onResizeEnd={endResize}
                            />
                        ))}
                    </Box>
                </SortableContext>

                {createPortal(
                    <DragOverlay zIndex={1500} dropAnimation={null}>
                        {activeColumn && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    px: 0.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.18)',
                                    cursor: 'grabbing'
                                }}
                            >
                                <HeaderCellContent label={activeColumn.label}/>
                            </Box>
                        )}
                    </DragOverlay>,
                    document.body
                )}
            </DndContext>
        </Box>
    );
}
